/**
 * Platform / Provider matrix.
 *
 * Single source of truth for which `(platform, provider)` combos exist and how
 * far along each one is. DB enum values for `property_integrations.platform`
 * and `property_integrations.provider` are derived from this matrix
 * (a unit test asserts they stay in sync). Settings UI, runtime routing
 * decisions, message dispatch, and stage planning all read from here.
 *
 * Stage progression is encoded by the `status` field — flip a row from
 * `planned` to `available` when its implementation ships.
 *
 * RETIREMENT RULE: a retired combo is marked `unavailable` and kept, never
 * deleted. Postgres cannot drop an enum value, so the id must stay in
 * `PLATFORM_IDS` / `PROVIDER_IDS` for the derived `integration_platform` and
 * `integration_provider` enums to keep covering rows already in the database;
 * removing it would make the API row contract (`z.enum(PLATFORM_IDS)`) throw on
 * an existing row. A retained row is not a claim that anything still syncs — it
 * is offered nowhere (no marketing card, no add path, no new connections).
 */

// Platform + Provider IDs
//
// Platforms describe WHAT we're connected to (whatsapp, booking_com, …).
// Providers describe HOW we get there (twilio, channex, direct, …).
//
// Both lists are intentionally exhaustive across stages A→C so the matrix
// stays the single roadmap. Status flips control what is actually live.

// `vrbo` is retired, retained per the retirement rule above. To offer it again
// in v2: flip vrbo:ical to `available` + re-add its channex/direct rows.
export const PLATFORM_IDS = [
  "whatsapp",
  "phone",
  "email",
  "instagram",
  "booking_com",
  "airbnb",
  "vrbo",
] as const;

export type PlatformId = (typeof PLATFORM_IDS)[number];

/**
 * OTAs we OFFER a distribution connection for. Excludes retired Vrbo.
 *
 * The tuple, not just the union, because `contracts/property-integrations.ts`
 * builds its wire `z.enum` from it — one list, so the type and the validator
 * cannot drift.
 */
export const OTA_PLATFORM_IDS = ["booking_com", "airbnb"] as const;
export type OtaPlatform = (typeof OTA_PLATFORM_IDS)[number];

/** Coarse bucket the settings screens group and label integrations by. */
export type PlatformGroup = "messaging" | "ota";

// Which bucket each platform belongs in. A property of the platform, not of the
// (platform, provider) combo: every provider row for a platform shares it, so
// it lives here once rather than repeated per matrix row where two rows for the
// same platform could disagree. Typed as a total Record, so adding a platform
// without classifying it is a compile error instead of silently landing in
// `messaging`. Retired platforms are still classified — Vrbo is an OTA whether
// or not it is offered.
const PLATFORM_GROUPS: Record<PlatformId, PlatformGroup> = {
  airbnb: "ota",
  booking_com: "ota",
  email: "messaging",
  instagram: "messaging",
  phone: "messaging",
  vrbo: "ota",
  whatsapp: "messaging",
};

// Provider IDs that have at least one row in the matrix
// below. Keep this in sync with the matrix — a provider listed here
// but absent from the matrix would type-check but silently drop out of
// the derived DB enum, breaking inserts.
//
// `channex` is the white-label channel-manager backend (Stage B): hosts
// see Booking.com / Airbnb connect cards, we drive Channex behind the
// scenes. `beds24` and `ical` are retired, retained per the
// retirement rule above. Never list a provider here without a matching
// matrix row.
export const PROVIDER_IDS = [
  "twilio",
  "meta_cloud_api",
  "meta_instagram",
  "resend",
  "ical",
  "channex",
  "beds24",
  "direct",
] as const;

export type ProviderId = (typeof PROVIDER_IDS)[number];

// Matrix entry shape

export type PlatformProviderStatus = "available" | "planned" | "unavailable";

export interface PlatformProviderEntry {
  brandColor?: string;
  displayName: string;
  iconKey?: string;
  /** Dev-facing context, not UI copy. */
  notes?: string;
  status: PlatformProviderStatus;
}

type MatrixKey = `${PlatformId}:${ProviderId}`;

// The matrix

export const PLATFORM_PROVIDER_MATRIX = {
  "airbnb:beds24": {
    brandColor: "#FF5A5F",
    displayName: "Airbnb (Live)",
    iconKey: "airbnb",
    notes: "Retired. Connector removed pre-go-live; Channex replaces it.",
    status: "unavailable",
  },
  "airbnb:channex": {
    brandColor: "#FF5A5F",
    displayName: "Airbnb (via Channex)",
    iconKey: "airbnb",
    notes:
      "Live wherever the environment holds channel-manager credentials; dormant everywhere else. This row alone does not activate the channel: the server gates on the credentials too, so an environment without them offers nothing and refuses a connect with a 503.",
    status: "available",
  },
  "airbnb:direct": {
    brandColor: "#FF5A5F",
    displayName: "Airbnb",
    iconKey: "airbnb",
    notes:
      "Invite-only partner programme. NDA + security review + 6-month implementation clock once accepted.",
    status: "planned",
  },
  "airbnb:ical": {
    brandColor: "#FF5A5F",
    displayName: "Airbnb (Calendar Sync)",
    iconKey: "airbnb",
    notes: "Retired. Superseded by Channex.",
    status: "unavailable",
  },

  // Beds24 (RETIRED, see the retirement rule at the top)
  //
  // The original Stage B white-label backend, removed before going live
  // (Channex is the chosen backend). The white-label rule holds even
  // in retirement: displayName never names the backend.
  "booking_com:beds24": {
    brandColor: "#003580",
    displayName: "Booking.com (Live)",
    iconKey: "booking_com",
    notes: "Retired. Connector removed pre-go-live; Channex replaces it.",
    status: "unavailable",
  },

  // OTAs via Channex (Stage B)
  "booking_com:channex": {
    brandColor: "#003580",
    displayName: "Booking.com (via Channex)",
    iconKey: "booking_com",
    notes:
      "Live wherever the environment holds channel-manager credentials; dormant everywhere else. This row alone does not activate the channel: the server gates on the credentials too, so an environment without them offers nothing and refuses a connect with a 503.",
    status: "available",
  },

  // OTAs direct (Stage C)
  "booking_com:direct": {
    brandColor: "#003580",
    displayName: "Booking.com",
    iconKey: "booking_com",
    notes: "Direct CPP. Programme paused; apply when reopens.",
    status: "planned",
  },

  // OTAs via iCal (RETIRED, see the retirement rule at the top)
  //
  // The Stage A bridge; gone (ADR-0079), Channex is the committed path.
  "booking_com:ical": {
    brandColor: "#003580",
    displayName: "Booking.com (Calendar Sync)",
    iconKey: "booking_com",
    notes: "Retired. Superseded by Channex.",
    status: "unavailable",
  },
  "email:resend": {
    brandColor: "#6B7280",
    displayName: "Email",
    iconKey: "email",
    notes:
      "Zero-setup guest email channel (SUP-145): shared receiving domain, address derived from the property slug. Runtime availability is env-gated via isEmailChannelConfigured().",
    status: "available",
  },
  "instagram:meta_instagram": {
    brandColor: "#E4405F",
    displayName: "Instagram",
    iconKey: "instagram",
    notes:
      "Feed (Places tile) + DMs as a guest channel (SUP-153), on the shared Meta app's nested Instagram Login app (ADR-0062). Distinct from meta_cloud_api: Instagram Login runs on graph.instagram.com with its own client_id/secret, and provider is the webhook reverse-lookup key, so sharing WhatsApp's provider would collide. Available since Advanced Access for instagram_business_basic + instagram_business_manage_messages; Human Agent replies (24h to 7d) are offered but refused by Meta until App Review approves the feature (INSTAGRAM_LATE_REPLIES_OFFERED in messaging-window.ts).",
    status: "available",
  },
  "phone:twilio": {
    brandColor: "#2563EB",
    displayName: "Phone",
    iconKey: "phone",
    notes:
      "Real Twilio voice/SMS wiring exists and a demo number is seeded for local dev, but nothing customer-facing has shipped: only dev/simulate-* routes exercise it. Flip to available once it's actually offered to customers.",
    status: "planned",
  },
  // Vrbo's only retained row (RETIRED, see the retirement rule at the top).
  "vrbo:ical": {
    brandColor: "#3D67FF",
    displayName: "Vrbo (Calendar Sync)",
    iconKey: "vrbo",
    notes: "Retired. Superseded by Channex.",
    status: "unavailable",
  },
  // Messaging
  // WhatsApp's only retained direct-Meta row (RETIRED, see the retirement rule
  // at the top). Onboarding, inbound, and outbound all run through Twilio as
  // BSP (ADR-0030); nothing writes a meta_cloud_api row any more.
  "whatsapp:meta_cloud_api": {
    brandColor: "#25D366",
    displayName: "WhatsApp",
    iconKey: "whatsapp",
    notes: "Retired. Superseded by the Twilio BSP path.",
    status: "unavailable",
  },
  "whatsapp:twilio": {
    brandColor: "#25D366",
    displayName: "WhatsApp",
    iconKey: "whatsapp",
    status: "available",
  },
} as const satisfies Partial<Record<MatrixKey, PlatformProviderEntry>>;

// Helpers

function matrixKey(platform: PlatformId, provider: ProviderId): MatrixKey {
  return `${platform}:${provider}` as MatrixKey;
}

/** Lookup a (platform, provider) combo. Returns undefined if absent. */
export function entry(
  platform: PlatformId,
  provider: ProviderId
): PlatformProviderEntry | undefined {
  return (PLATFORM_PROVIDER_MATRIX as Record<string, PlatformProviderEntry>)[
    matrixKey(platform, provider)
  ];
}

/**
 * Platforms present in the matrix, optionally filtered to those that have
 * at least one provider with the given status.
 */
export function listPlatforms(opts?: {
  status?: PlatformProviderStatus;
}): PlatformId[] {
  const filterStatus = opts?.status;
  const seen = new Set<PlatformId>();
  const ordered: PlatformId[] = [];
  for (const platform of PLATFORM_IDS) {
    for (const provider of PROVIDER_IDS) {
      const e = entry(platform, provider);
      if (!e) {
        continue;
      }
      if (filterStatus && e.status !== filterStatus) {
        continue;
      }
      if (!seen.has(platform)) {
        seen.add(platform);
        ordered.push(platform);
      }
    }
  }
  return ordered;
}

/** Providers known for a platform, in matrix order. */
export function listProvidersForPlatform(platform: PlatformId): ProviderId[] {
  const out: ProviderId[] = [];
  for (const provider of PROVIDER_IDS) {
    if (entry(platform, provider)) {
      out.push(provider);
    }
  }
  return out;
}

/** Aggregate platform status from its provider rows. */
export function platformStatus(platform: PlatformId): PlatformProviderStatus {
  let best: PlatformProviderStatus = "unavailable";
  for (const provider of listProvidersForPlatform(platform)) {
    const s = entry(platform, provider)?.status;
    if (s === "available") {
      return "available";
    }
    if (s === "planned") {
      best = "planned";
    }
  }
  return best;
}

// Brand identity (badges, chips, channel labels)
//
// Bare brand name + color for a platform, as shown on badges/chips across the
// apps. A matrix entry's `displayName` names a specific *integration*
// ("Booking.com (Calendar Sync)", "Booking.com (via Channex)"); the brand name
// strips that qualifier so the same surface reads as "Booking.com". Colors come
// from the entries' `brandColor`, which is consistent across a platform's
// providers.

/**
 * Representative matrix entry for a platform: the first `available` provider's
 * entry, else the first known entry. Undefined when the platform has no rows.
 */
function representativeEntry(
  platform: PlatformId
): PlatformProviderEntry | undefined {
  const providers = listProvidersForPlatform(platform);
  for (const provider of providers) {
    const e = entry(platform, provider);
    if (e?.status === "available") {
      return e;
    }
  }
  return providers[0] ? entry(platform, providers[0]) : undefined;
}

/**
 * Human-facing brand name for a platform ("Booking.com", "WhatsApp"), stripped
 * of any integration qualifier. Falls back to the platform id if the platform
 * has no matrix entry.
 */
const DISPLAY_NAME_QUALIFIER_RE = / \(.*\)$/u;

export function platformDisplayName(platform: PlatformId): string {
  const e = representativeEntry(platform);
  return e ? e.displayName.replace(DISPLAY_NAME_QUALIFIER_RE, "") : platform;
}

/**
 * Brand color for a platform, read from the same representative entry as
 * `platformDisplayName` so a badge's name and color always come from one row.
 * Undefined when that entry sets none.
 */
export function platformBrandColor(platform: PlatformId): string | undefined {
  return representativeEntry(platform)?.brandColor;
}

// DB-enum derivation
//
// The Drizzle schema imports these to populate the `integration_platform`
// and `integration_provider` pgEnums. A unit test asserts the arrays are
// non-empty and stay in sync with the matrix's keys — drift fails CI.

function deriveProviderIds(): readonly ProviderId[] {
  const seen = new Set<ProviderId>();
  const ordered: ProviderId[] = [];
  for (const platform of PLATFORM_IDS) {
    for (const provider of PROVIDER_IDS) {
      if (!entry(platform, provider)) {
        continue;
      }
      if (!seen.has(provider)) {
        seen.add(provider);
        ordered.push(provider);
      }
    }
  }
  return ordered;
}

export const integrationPlatformValues =
  listPlatforms() as readonly PlatformId[] as readonly [
    PlatformId,
    ...PlatformId[],
  ];

export const integrationProviderValues =
  deriveProviderIds() as readonly ProviderId[] as readonly [
    ProviderId,
    ...ProviderId[],
  ];

// UI grouping (Messaging vs OTA)

export function platformGroup(platform: PlatformId): PlatformGroup {
  return PLATFORM_GROUPS[platform];
}
